import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { showToast } from '@quickmd-llc/design-system'
import { useLocation, useNavigate } from 'react-router-dom'
import { useCreateMember } from './hooks/useCreateMember'
import { useValidateMemberPhone } from './hooks/useValidateMemberPhone'
import { validateCreateMember } from './hooks/validateCreateMember'
import { buildCreateAdminRequest, buildCreateProviderRequest } from './hooks/buildCreateMemberRequest'
import { mapMemberError } from './hooks/memberRequestErrors'
import type { DuplicateLicensesInfo } from './hooks/memberRequestErrors'
import type { LegacyLicense } from './helpers/licenseShapeAdapter'
import { getBreadCrumbs } from './helpers/getBreadcrumbs'
import { GeneralInformationSection } from './components/GeneralInformationSection'
import type { FormFieldType } from './components/GeneralInformationSection'
import { LicenseInformationSection } from './components/LicenseInformationSection'
import { BookingAccessSection } from './components/BookingAccessSection'
import { NewMemberFooter } from './components/NewMemberFooter'
import { licenseFormSchema } from '../member/license-form/LicenseFormSchema'
import type { LicenseFormValues } from '../member/license-form/LicenseFormSchema'
import { errorCodes, getUserFriendlyErrorMessage } from '../../utils/errormessages'
import { useServiceTypes } from '../../hooks/useServiceTypes'
import { flattenServiceTypes } from '../member/license-form/flattenServiceTypes'
import { useLicenseFormServiceTypes } from '../member/license-form/useLicenseFormServiceTypes'
import { getVisibilityType } from '../member/Constants'
import { useAppSelector } from '../../redux/hooks/useAppSelector'
import { Breadcrumb } from '@/components/breadcrumb/Breadcrumb'
import { EditPageShell } from '@/components/page-shell/EditPageShell'

const NewMembers = memo(() => {
  const navigate = useNavigate()
  const location = useLocation()

  // pageState is still driven by EditMemberImage (image sizing → SET_PAGE_STATUS) for the
  // button spinner; create/image-upload/phone now run through React Query (see below).
  const pageState = useAppSelector((state) => state.newClinicMember.pageState)
  const { isLoading: loadingServicesTypes } = useServiceTypes()
  // Same underlying React Query data as useServiceTypes, normalized to the strict ServiceType
  // shape flattenServiceTypes consumes (matches LicenseFormPage/EditLicensePage).
  const serviceTypes = useLicenseFormServiceTypes()
  const loadingServiceType = useAppSelector((state) => state.viewService.loading)

  const { createMember, isPending: isCreatingMember } = useCreateMember()
  const { validatePhone } = useValidateMemberPhone()

  /* Local replacements for the former newClinicMember slice fields. */
  const [error, setError] = useState('')
  const [duplicateLicensesInfo, setDuplicateLicensesInfo] = useState<DuplicateLicensesInfo | null>(null)
  const [phoneIsValid, setPhoneIsValid] = useState<boolean | null>(null)

  /* Mirrors the old `setError` action creator: maps an error code → friendly message
     (empty code clears the alert), matching the previous SET_NEW_MEMBER_PAGE_ERROR reducer. */
  const setErrorByCode = useCallback((code: number | string = '', extra: unknown = null) => {
    const extraStr = typeof extra === 'string' ? extra : ''
    setError(code ? getUserFriendlyErrorMessage(code, null, extraStr) : '')
  }, [])

  /* ─── Form state ──────────────────────────────────────────────────────────── */
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState(false)
  const [emailLocalError, setEmailLocalError] = useState('')

  const [firstName, setFirstName] = useState('')
  const [firstNameError, setFirstNameError] = useState(false)
  const [firstNameLocalError, setFirstNameLocalError] = useState('')

  const [prefix, setPrefix] = useState(' ')

  const [lastName, setLastName] = useState('')
  const [lastNameError, setLastNameError] = useState(false)
  const [lastNameLocalError, setLastNameLocalError] = useState('')

  const [suffix, setSuffix] = useState(' ')
  const [role, setRole] = useState('')
  const [roleError, setRoleError] = useState(false)
  const [roleLocalError, setRoleLocalError] = useState('')

  const [profileImgInputValue, setProfileImgInputValue] = useState<string | Blob>('')
  const [isImageChanged, setIsImageChanged] = useState(false)

  const [professionalTitle, setProfessionalTitle] = useState('')
  const [professionalTitleError, setProfessionalTitleError] = useState(false)
  const [professionalTitleLocalError, setProfessionalTitleLocalError] = useState('')

  const [npi, setNpi] = useState('')

  /* ─── License form (react-hook-form) ─────────────────────────────────────── */
  const licenseForm = useForm<LicenseFormValues>({
    resolver: zodResolver(licenseFormSchema),
    defaultValues: {
      stateCode: '',
      licenseEntries: [{ type: 'Medical', number: '', deaStateCode: '' }],
      visitTypes: [],
    },
    mode: 'onChange',
  })
  const {
    fields: licenseFields,
    append: appendEntry,
    remove: removeEntry,
  } = useFieldArray({
    control: licenseForm.control,
    name: 'licenseEntries',
  })
  const watchedEntries = licenseForm.watch('licenseEntries')

  /* Phone — hardcoded to US (+1) */
  const [phoneNumber, setPhoneNumber] = useState('')
  const [isPhoneCorrect, setIsPhoneCorrect] = useState<boolean | ''>('')
  const [phoneLocalError, setPhoneLocalError] = useState('')
  const [lastPhoneLocalError, setLastLocalPhoneError] = useState('')
  const [oldNumber, setOldNumber] = useState('')
  const [isOldNumberCorrect, setIsOldNumberCorrect] = useState<boolean | ''>('')
  const [phoneCheckLoading, setPhoneCheckLoading] = useState(false)

  const [isExcludedFromMarketingReports, setIsExcludedFromMarketingReports] = useState(false)

  /* ─── Build licensesArray for submit ─────────────────────────────────────── */
  const buildLicensesArrayForSubmit = useCallback((): LegacyLicense[] => {
    const { stateCode, licenseEntries: entries, visitTypes } = licenseForm.getValues()

    const selectedVTs = visitTypes
      .filter((vt) => vt.isSelected)
      .map((vt) => ({
        VisitTypeId: vt.visitTypeId,
        // ServiceTypeId/name/VisitTypes are required by LegacyVisitTypeOption but unused by the
        // create request body (buildCreateProviderRequest reads only isSelected/visibilityType/
        // VisitTypeId); defaulted to empty to satisfy the type without changing behavior.
        ServiceTypeId: '',
        name: '',
        VisitTypes: [],
        value: vt.visitTypeId,
        isSelected: true,
        isActive: true,
        visibilityType: getVisibilityType(vt.visibilityOverride),
      }))

    const medicalEntry = entries.find((e) => e.type === 'Medical')
    const cslEntry = entries.find((e) => e.type === 'CSL')
    const deaEntry = entries.find((e) => e.type === 'DEA')

    return [
      {
        licenseBlockId: `autoGenerated_${crypto.randomUUID()}`,
        licenseVisitTypesOptions: selectedVTs,
        licenseVisitTypesDropdownValue: selectedVTs[0]?.value ?? '',
        licenseNumberValue: medicalEntry?.number ?? '',
        cslNumber: cslEntry?.number ?? null,
        deaNumber: deaEntry?.number ?? null,
        deaStateCode: deaEntry?.deaStateCode ?? null,
        stateLicenseValue: stateCode,
        licensedByValue: 'Medical',
        licenseVisitTypesDropdownValueError: selectedVTs.length === 0,
        licenseVisitTypesDropdownValueLocalError: selectedVTs.length === 0 ? 'At least one visit type is required' : '',
        licenseNumberError: false,
        licenseNumberLocalError: '',
        licenseStateLicenseValueError: !stateCode,
        licenseStateLicenseValueLocalError: !stateCode ? 'State is required' : '',
      },
    ]
  }, [licenseForm])

  /* Maps setLicensesArray error-update calls back to RHF setError */
  const syncLicenseErrors = useCallback(
    (updaterOrArray: LegacyLicense[] | ((prev: LegacyLicense[]) => LegacyLicense[])) => {
      const la = buildLicensesArrayForSubmit()
      const updated = typeof updaterOrArray === 'function' ? updaterOrArray(la) : updaterOrArray
      updated.forEach((entry, idx) => {
        if (entry.licenseNumberError && entry.licenseNumberLocalError) {
          const message = typeof entry.licenseNumberLocalError === 'string' ? entry.licenseNumberLocalError : ''
          licenseForm.setError(`licenseEntries.${idx}.number`, { message })
        }
        if (entry.licenseStateLicenseValueError) {
          const stateMessage =
            typeof entry.licenseStateLicenseValueLocalError === 'string' && entry.licenseStateLicenseValueLocalError ?
              entry.licenseStateLicenseValueLocalError
            : 'State is required'
          licenseForm.setError('stateCode', { message: stateMessage })
        }
      })
    },
    [buildLicensesArrayForSubmit, licenseForm],
  )

  /* ─── Effects ───────────────────────────────────────────────────────────── */

  // Reset phone + license state when role changes
  useEffect(() => {
    setPhoneNumber('')
    setIsPhoneCorrect('')
    setPhoneLocalError('')
    setOldNumber('')
    setIsOldNumberCorrect('')
    setLastLocalPhoneError('')
    licenseForm.reset({
      stateCode: '',
      licenseEntries: [{ type: 'Medical', number: '', deaStateCode: '' }],
      visitTypes: [],
    })
    if (role !== 'Provider') {
      setIsExcludedFromMarketingReports(false)
    }
  }, [role, licenseForm])

  // Initialize visit types when service types load
  useEffect(() => {
    if (role === 'Provider' && serviceTypes?.length) {
      const flatVTs = flattenServiceTypes(serviceTypes)
      if (flatVTs.length > 0 && !licenseForm.getValues('visitTypes').length) {
        licenseForm.reset((prev) => ({ ...prev, visitTypes: flatVTs }), { keepDirty: true })
      }
    }
  }, [serviceTypes, role, licenseForm])

  // Reflect the latest phone-validation result in the inline phone UI.
  useEffect(() => {
    if (phoneIsValid !== null) {
      setIsPhoneCorrect(phoneIsValid)
      setIsOldNumberCorrect(phoneIsValid)
      const errorMsg = getUserFriendlyErrorMessage(errorCodes.ENTER_VALID, null, 'phone number')
      if (!phoneIsValid) {
        setPhoneLocalError(errorMsg)
        setLastLocalPhoneError(errorMsg)
      }
    }
  }, [phoneIsValid])

  // Duplicate license info from server
  useEffect(() => {
    if (duplicateLicensesInfo) {
      const { stateCode, licenseEntries: entries } = licenseForm.getValues()
      if (stateCode === duplicateLicensesInfo.stateCode) {
        entries.forEach((entry, idx) => {
          if (
            entry.number === duplicateLicensesInfo.licenseNumber &&
            entry.type?.trim() === duplicateLicensesInfo.licensedBy?.trim()
          ) {
            licenseForm.setError(`licenseEntries.${idx}.number`, {
              message: getUserFriendlyErrorMessage(errorCodes.DUPLICATE_LICENSES_FROM_SERVER),
            })
          }
        })
      }
    }
  }, [duplicateLicensesInfo, licenseForm])

  // General error handling
  useEffect(() => {
    if (
      error === getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS) ||
      error === getUserFriendlyErrorMessage(errorCodes.NO_CONNECTION) ||
      error === getUserFriendlyErrorMessage(errorCodes.DEFAULT) ||
      error === getUserFriendlyErrorMessage(errorCodes.NAME_ALREADY_EXISTS, null, 'Email') ||
      error === getUserFriendlyErrorMessage(errorCodes.PROVIDER_NUMBER_NOT_UNIQUE)
    ) {
      setTimeout(() => {
        setError('')
      }, 3500)
    }
    if (error === getUserFriendlyErrorMessage(errorCodes.PROVIDER_NUMBER_NOT_UNIQUE)) {
      setLastLocalPhoneError(getUserFriendlyErrorMessage(errorCodes.PROVIDER_NUMBER_NOT_UNIQUE))
      setPhoneLocalError(getUserFriendlyErrorMessage(errorCodes.PROVIDER_NUMBER_NOT_UNIQUE))
      setIsOldNumberCorrect(false)
    }
    if (error === getUserFriendlyErrorMessage(errorCodes.NAME_ALREADY_EXISTS, null, 'Email')) {
      setEmailLocalError(getUserFriendlyErrorMessage(errorCodes.NAME_ALREADY_EXISTS, null, 'Email'))
    }
  }, [error])

  useEffect(() => {
    if (error === getUserFriendlyErrorMessage(errorCodes.DUPLICATE_LICENSES, null)) {
      const { licenseEntries: entries } = licenseForm.getValues()
      entries.forEach((_, idx) => {
        licenseForm.setError(`licenseEntries.${idx}.number`, {
          message: getUserFriendlyErrorMessage(errorCodes.DUPLICATE_LICENSES, null),
        })
      })
    }
  }, [error, licenseForm])

  // Cleanup on unmount
  useEffect(
    () => () => {
      setError('')
      setEmailLocalError('')
    },
    [],
  )

  /* ─── Error toast ──────────────────────────────────────────────────────────
     Replaces the inline ErrorAlert with the DS showToast. Same trigger conditions and same
     message text as the former `generateErrorAlert`, so the validation-error text stays
     assertable (the Task-4 spec matches "Please complete all requirements."). */
  useEffect(() => {
    const fillAllFields = getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS)
    const noConnection = getUserFriendlyErrorMessage(errorCodes.NO_CONNECTION)
    const defaultError = getUserFriendlyErrorMessage(errorCodes.DEFAULT)
    const nameAlreadyExists = getUserFriendlyErrorMessage(errorCodes.NAME_ALREADY_EXISTS, null, 'Email')
    const providerNumberNotUnique = getUserFriendlyErrorMessage(errorCodes.PROVIDER_NUMBER_NOT_UNIQUE)
    const duplicateLicenses = getUserFriendlyErrorMessage(errorCodes.DUPLICATE_LICENSES, null)

    if (error === noConnection || error === fillAllFields || error === defaultError) {
      showToast({ type: 'error', message: error, position: 'bottom-right' })
    } else if (error === nameAlreadyExists || error === providerNumberNotUnique) {
      showToast({ type: 'error', message: fillAllFields, position: 'bottom-right' })
    } else if (error === duplicateLicenses) {
      showToast({ type: 'error', message: duplicateLicenses, position: 'bottom-right' })
    }
  }, [error])

  /* ─── Derived values ────────────────────────────────────────────────────── */

  const emailFieldError = useMemo(
    () =>
      (
        error === getUserFriendlyErrorMessage(errorCodes.ENTER_VALID, null, 'Email') ||
        error === getUserFriendlyErrorMessage(errorCodes.NAME_ALREADY_EXISTS, null, 'Email')
      ) ?
        error
      : emailLocalError,
    [emailLocalError, error],
  )

  const firstNameFieldError = useMemo(
    () =>
      error === getUserFriendlyErrorMessage(errorCodes.ENTER_VALID, null, 'First name') ?
        getUserFriendlyErrorMessage(errorCodes.ENTER_VALID, null, 'First name')
      : firstNameLocalError,
    [error, firstNameLocalError],
  )

  const lastNameFieldError = useMemo(
    () =>
      error === getUserFriendlyErrorMessage(errorCodes.ENTER_VALID, null, 'Last name') ?
        getUserFriendlyErrorMessage(errorCodes.ENTER_VALID, null, 'Last name')
      : lastNameLocalError,
    [error, lastNameLocalError],
  )

  const isLoading = useMemo(
    () => pageState === 'loading' || isCreatingMember || loadingServicesTypes || loadingServiceType,
    [isCreatingMember, loadingServiceType, loadingServicesTypes, pageState],
  )

  const isErrorPhoneNumberInput = useMemo(
    () =>
      Boolean(phoneLocalError) ||
      (phoneNumber === oldNumber && !isOldNumberCorrect && isOldNumberCorrect !== '' && !isLoading),
    [isLoading, isOldNumberCorrect, oldNumber, phoneLocalError, phoneNumber],
  )

  /* ─── Handlers ──────────────────────────────────────────────────────────── */

  const onSetFormValues = useCallback(
    (type: FormFieldType, value: string) => {
      switch (type) {
        case 'email': {
          setEmailLocalError('')
          setEmailError(false)
          setError('')
          if (value?.length < 226) setEmail(value)
          break
        }
        case 'firstName': {
          setFirstNameLocalError('')
          setFirstNameError(false)
          if (value?.length < 51) setFirstName(value)
          break
        }
        case 'lastName': {
          setLastNameLocalError('')
          setLastNameError(false)
          if (value?.length < 51) setLastName(value)
          break
        }
        case 'role': {
          setRoleLocalError('')
          setRoleError(false)
          setRole(value)
          break
        }
        case 'ProfessionalTitle': {
          setProfessionalTitleLocalError('')
          setProfessionalTitleError(false)
          setProfessionalTitle(value)
          break
        }
        case 'phoneNumber': {
          if (!Number.isNaN(Number(value)) && !value.endsWith('.')) {
            setPhoneLocalError('')
            if (error) setError('')
            if (value === oldNumber && !isOldNumberCorrect && isOldNumberCorrect !== '') {
              setPhoneLocalError(lastPhoneLocalError)
              setIsPhoneCorrect(false)
            } else if (value === oldNumber && isOldNumberCorrect) {
              setIsPhoneCorrect(true)
            } else {
              setIsPhoneCorrect(false)
            }
            setPhoneNumber(value)
          }
          break
        }
        default:
          break
      }
    },
    [error, isOldNumberCorrect, lastPhoneLocalError, oldNumber],
  )

  const onBlur = useCallback(() => {
    if (phoneNumber === oldNumber) {
      setIsPhoneCorrect(isOldNumberCorrect ? true : false)
    }
    setTimeout(() => {
      if (phoneNumber && phoneNumber !== oldNumber) {
        setOldNumber(phoneNumber)
        setPhoneIsValid(null)
        void validatePhone({
          Number: `+1${phoneNumber}`,
          Prefix: '+1',
          CountryCode: 'US',
        }).then((result) => {
          if (result.error) setError(result.error)
          setPhoneIsValid(result.phoneIsValid)
        })
      }
    }, 150)
  }, [validatePhone, isOldNumberCorrect, oldNumber, phoneNumber])

  const onCancelPress = useCallback(() => {
    void navigate(`/management${location.search || ''}`)
  }, [navigate, location.search])

  const onCreateNewAdminMember = useCallback(
    async (isPhoneValidated: boolean | '', phoneError: string) => {
      let phoneValidated = isPhoneValidated
      let phoneErr: string = phoneError

      // If a phone number was entered but not yet confirmed valid, validate it now and use the
      // result to gate the submit — equivalent to the old "wait for VALIDATE_PHONE_NUMBER then
      // re-run create" dance, but awaited inline.
      if (phoneNumber && !phoneValidated && !phoneErr) {
        setPhoneCheckLoading(true)
        const result = await validatePhone({
          Number: `+1${phoneNumber}`,
          Prefix: '+1',
          CountryCode: 'US',
        })
        setPhoneCheckLoading(false)
        setOldNumber(phoneNumber)
        setPhoneIsValid(result.phoneIsValid)
        phoneValidated = result.phoneIsValid
        const invalidMsg = getUserFriendlyErrorMessage(errorCodes.ENTER_VALID, null, 'phone number')
        phoneErr = result.phoneIsValid ? '' : result.error || invalidMsg
        if (result.error) setError(result.error)
      }

      // Validate license form before submitting (Provider only)
      if (role === 'Provider') {
        const isLicenseValid = await licenseForm.trigger()
        if (!isLicenseValid) return
      }

      const la = buildLicensesArrayForSubmit()

      const isProviderWithLicense = role === 'Provider'
      const isValid = validateCreateMember(
        {
          firstName,
          lastName,
          email,
          role,
          professionalTitle,
          phoneNumberError: phoneErr,
          licensesArray: la,
          isProviderWithLicense,
        },
        {
          setPageError: setErrorByCode,
          setFirstNameLocalError,
          setFirstNameError,
          setLastNameLocalError,
          setLastNameError,
          setEmailLocalError,
          setEmailError,
          setRoleLocalError,
          setRoleError,
          setProfessionalTitleLocalError,
          setProfessionalTitleError,
          setLicensesArray: syncLicenseErrors,
        },
      )

      if (!isValid) return

      const phonePayload = phoneNumber ? { Number: `+1${phoneNumber}`, Prefix: '+1', CountryCode: 'US' } : null

      const sharedFields = { email, firstName, lastName, prefix, suffix }
      // The image is always a File (a Blob) when set; the empty-string default collapses to null,
      // matching the old `profileImgInputValue || null`.
      const image = profileImgInputValue instanceof Blob ? profileImgInputValue : null

      try {
        if (role === 'Admin') {
          await createMember({ role: 'Admin', body: buildCreateAdminRequest(sharedFields), image })
        } else {
          await createMember({
            role: 'Provider',
            body: buildCreateProviderRequest(sharedFields, {
              phoneNumber: phonePayload,
              professionalTitle,
              isExcludedFromMarketingReports,
              npi,
              productivityData: [],
              isProviderWithLicense,
              licensesArray: la,
            }),
            image,
          })
        }
        void navigate(`/management${location.search || ''}`)
      } catch (e) {
        const mapped = mapMemberError(e)
        setError(mapped.error)
        if (mapped.phoneIsValid === false) {
          setPhoneIsValid(false)
        }
        if (mapped.duplicateLicensesInfo) {
          setDuplicateLicensesInfo(mapped.duplicateLicensesInfo)
        }
      }
    },
    [
      phoneNumber,
      validatePhone,
      role,
      licenseForm,
      firstName,
      lastName,
      email,
      prefix,
      suffix,
      professionalTitle,
      isExcludedFromMarketingReports,
      buildLicensesArrayForSubmit,
      syncLicenseErrors,
      setErrorByCode,
      createMember,
      navigate,
      location.search,
      profileImgInputValue,
      npi,
    ],
  )

  const onCreateNewAdminMemberBtnPress = useCallback(() => {
    void onCreateNewAdminMember(isPhoneCorrect, phoneLocalError)
  }, [isPhoneCorrect, onCreateNewAdminMember, phoneLocalError])

  const onChangeRoleDropdownValue = useCallback(
    (value: string) => {
      onSetFormValues('role', value)
      licenseForm.reset({
        stateCode: '',
        licenseEntries: [{ type: 'Medical', number: '', deaStateCode: '' }],
        visitTypes: [],
      })
    },
    [onSetFormValues, licenseForm],
  )

  /* ─── Render ─────────────────────────────────────────────────────────────── */
  const crumbs = getBreadCrumbs(location, navigate)

  return (
    <EditPageShell
      breadcrumb={
        <>
          <Breadcrumb crumbs={crumbs} />
          <h1 className='m-0 mb-sp-1 text-size-6 font-semibold leading-8 text-text-dark' data-testid='pw_nm-heading'>
            New member
          </h1>
        </>
      }
      footer={
        <NewMemberFooter
          isLoading={isLoading || phoneCheckLoading}
          onCancel={onCancelPress}
          onSendInvite={onCreateNewAdminMemberBtnPress}
        />
      }>
      <GeneralInformationSection
        role={role}
        profileImgInputValue={profileImgInputValue}
        setProfileImgInputValue={setProfileImgInputValue}
        isImageChanged={isImageChanged}
        setIsImageChanged={setIsImageChanged}
        roleError={roleError}
        roleLocalError={roleLocalError}
        onChangeRole={onChangeRoleDropdownValue}
        prefix={prefix}
        setPrefix={setPrefix}
        suffix={suffix}
        setSuffix={setSuffix}
        firstName={firstName}
        firstNameError={firstNameError}
        firstNameFieldError={firstNameFieldError}
        lastName={lastName}
        lastNameError={lastNameError}
        lastNameFieldError={lastNameFieldError}
        email={email}
        emailError={emailError}
        emailFieldError={emailFieldError}
        phoneNumber={phoneNumber}
        phoneLocalError={phoneLocalError}
        isErrorPhoneNumberInput={isErrorPhoneNumberInput}
        onPhoneBlur={onBlur}
        professionalTitle={professionalTitle}
        professionalTitleError={professionalTitleError}
        professionalTitleLocalError={professionalTitleLocalError}
        npi={npi}
        setNpi={setNpi}
        isExcludedFromMarketingReports={isExcludedFromMarketingReports}
        setIsExcludedFromMarketingReports={setIsExcludedFromMarketingReports}
        onSetFormValues={onSetFormValues}
      />

      {role === 'Provider' && (
        <LicenseInformationSection
          licenseForm={licenseForm}
          licenseFields={licenseFields}
          appendEntry={appendEntry}
          removeEntry={removeEntry}
          watchedEntries={watchedEntries}
        />
      )}

      {role === 'Provider' && <BookingAccessSection licenseForm={licenseForm} />}
    </EditPageShell>
  )
})

export default NewMembers
